// ===== SAC · Funil de atendimento =====
// Referência de modelo: ciclo de atendimento em 4 marcos (entrada → atendimento →
// resolução → encerramento com avaliação), com a espera tratada como desvio lateral
// — é ela que trava o funil, não uma etapa que todo chamado precisa cumprir.
import React from 'react';
import { Icon } from './icons.jsx';
import { SAC_STATUS_BY_ID, SAC_TIPOS_BY_ID, TODAY, sacFmtHoras, sacSla } from './data.jsx';
import { ChamadoCard } from './sac.jsx';

const { useState: useStateSacF, useMemo: useMemoSacF } = React;

// Posição de cada status na linha do tempo do chamado (union dos dois ciclos).
const SACF_RANK = {
  aberto: 0, em_atendimento: 1,
  aguardando_cliente: 2, aguardando_retorno: 2, produto_recebido: 2, laude_emitido: 2, em_correcao: 2,
  resolvido: 3, fechado: 4,
};
const SACF_ESPERA = ["aguardando_cliente", "aguardando_retorno", "produto_recebido", "laude_emitido", "em_correcao"];

const SACF_ETAPAS = [
  { id: "entrada",     label: "Entrada",         icon: "life-buoy",      rankMin: 0, atual: ["aberto"],          curto: "entrada",       desc: "Protocolado, aguardando triagem" },
  { id: "atendimento", label: "Em atendimento",  icon: "wrench",         rankMin: 1, atual: ["em_atendimento"],  curto: "atendimento",   desc: "Equipe assumiu e está tratando" },
  { id: "resolvido",   label: "Resolvido",       icon: "check-circle-2", rankMin: 3, atual: ["resolvido"],       curto: "resolvido",     desc: "Solução entregue, aguardando avaliação" },
  { id: "encerrado",   label: "Encerrado",       icon: "smile",          rankMin: 4, atual: ["fechado"],         curto: "encerrado",     desc: "Fechado após CSAT ou 48h sem resposta" },
];

function sacfMedia(arr) { return arr.length ? arr.reduce((s, n) => s + n, 0) / arr.length : 0; }
function sacfIdadeH(c) { return (TODAY - c.abertoEm) / 3600e3; }

function SacFunil({ chamados, onOpen }) {
  const [sel, setSel] = useStateSacF("entrada");

  const f = useMemoSacF(() => {
    const rank = (c) => SACF_RANK[c.status] ?? 0;
    const total = chamados.length;
    const etapas = SACF_ETAPAS.map((e) => {
      const aqui = chamados.filter((c) => e.atual.includes(c.status));
      const alcancou = chamados.filter((c) => rank(c) >= e.rankMin).length;
      const mix = { ok: 0, risco: 0, estourado: 0, pausado: 0 };
      aqui.forEach((c) => { const s = sacSla(c).status; if (mix[s] != null) mix[s]++; else mix.ok++; });
      return { ...e, aqui, alcancou, mix, pct: total ? Math.round((alcancou / total) * 100) : 0 };
    });
    const espera = chamados.filter((c) => SACF_ESPERA.includes(c.status));
    const esperaPorStatus = SACF_ESPERA
      .map((id) => ({ id, label: (SAC_STATUS_BY_ID[id] || {}).label || id, n: espera.filter((c) => c.status === id).length }))
      .filter((x) => x.n > 0);
    const encerrados = chamados.filter((c) => rank(c) >= 3);
    const noPrazo = encerrados.filter((c) => {
      const tipo = SAC_TIPOS_BY_ID[c.tipo];
      return c.resolvidoEm && tipo && (c.resolvidoEm - c.abertoEm) / 3600e3 <= tipo.slaH;
    }).length;
    const comCsat = chamados.filter((c) => c.csat != null);
    return {
      total, etapas, espera, esperaPorStatus,
      tmr: sacfMedia(encerrados.filter((c) => c.resolvidoEm).map((c) => (c.resolvidoEm - c.abertoEm) / 3600e3)),
      slaPct: encerrados.length ? Math.round((noPrazo / encerrados.length) * 100) : null,
      csat: comCsat.length ? Math.round((sacfMedia(comCsat.map((c) => c.csat))) * 10) / 10 : null,
      reaberturas: chamados.reduce((s, c) => s + (c.reaberturas || 0), 0),
    };
  }, [chamados]);

  const metrica = (e) => {
    if (e.id === "resolvido") return `TMR ${sacFmtHoras(Math.round(f.tmr))}${f.slaPct != null ? ` · ${f.slaPct}% no prazo` : ""}`;
    if (e.id === "encerrado") return `${f.csat != null ? `CSAT ${f.csat}/5` : "sem CSAT"}${f.reaberturas ? ` · ${f.reaberturas} reabertura${f.reaberturas > 1 ? "s" : ""}` : ""}`;
    if (!e.aqui.length) return "nenhum parado aqui";
    return `idade média ${sacFmtHoras(Math.round(sacfMedia(e.aqui.map(sacfIdadeH))))}`;
  };

  const lista = sel === "espera" ? f.espera : (f.etapas.find((e) => e.id === sel)?.aqui || []);
  const listaOrd = lista.slice().sort((a, b) => sacSla(a).restanteH - sacSla(b).restanteH);
  const selLabel = sel === "espera" ? "Em espera" : (f.etapas.find((e) => e.id === sel)?.label || "");
  const Card = ChamadoCard;

  if (!f.total) return <div className="empty" style={{ padding: 50 }}>Nenhum chamado neste filtro.</div>;

  return (
    <div className="sacf-wrap">
      <div className="sacf">
        <div className="sacf-head">
          <div className="sacf-tt"><Icon name="life-buoy" size={14} /> Funil de atendimento <span className="mono">{f.total}</span></div>
          <div className="sacf-legend">
            <span className="lg passou"><i />já avançou</span>
            <span className="lg ok"><i />em dia</span>
            <span className="lg risco"><i />em risco</span>
            <span className="lg venc"><i />SLA vencido</span>
            <span className="lg pausado"><i />SLA pausado</span>
          </div>
        </div>

        <div className="sacf-rows">
          {f.etapas.map((e, i) => {
            const prev = i > 0 ? f.etapas[i - 1] : null;
            const conv = prev ? (prev.alcancou ? Math.round((e.alcancou / prev.alcancou) * 100) : 0) : null;
            const passou = e.alcancou - e.aqui.length;
            const seg = (n) => (e.alcancou ? (n / e.alcancou) * 100 : 0);
            return (
              <React.Fragment key={e.id}>
                {prev && (
                  <div className="sacf-gap">
                    <Icon name="chevron-down" size={12} />
                    <span className="conv mono">{conv}%</span>
                    <span>avançaram</span>
                    {prev.aqui.length > 0 && <span className="leak">{prev.aqui.length} parado{prev.aqui.length > 1 ? "s" : ""} em {prev.curto}</span>}
                  </div>
                )}
                <button className={`sacf-row ${sel === e.id ? "on" : ""} ${e.mix.estourado ? "alert" : ""}`} onClick={() => setSel(e.id)}>
                  <span className="sacf-ic"><Icon name={e.icon} size={15} /></span>
                  <div className="sacf-lb">
                    <div className="nm">{e.label}</div>
                    <div className="ds">{e.desc}</div>
                  </div>
                  <div className="sacf-bar-wrap">
                    <div className="sacf-bar" style={{ width: `${Math.max(e.pct, 4)}%` }}>
                      <span className="s passou" style={{ width: `${seg(passou)}%` }} />
                      <span className="s ok" style={{ width: `${seg(e.mix.ok)}%` }} />
                      <span className="s risco" style={{ width: `${seg(e.mix.risco)}%` }} />
                      <span className="s venc" style={{ width: `${seg(e.mix.estourado)}%` }} />
                      <span className="s pausado" style={{ width: `${seg(e.mix.pausado)}%` }} />
                    </div>
                    <span className="sacf-n mono">{e.alcancou}<span className="pc">{e.pct}%</span></span>
                  </div>
                  <div className="sacf-metrics">
                    <div className="agora mono">{e.aqui.length} aqui</div>
                    <div className="sub">{metrica(e)}</div>
                  </div>
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {f.espera.length > 0 && (
          <button className={`sacf-espera ${sel === "espera" ? "on" : ""}`} onClick={() => setSel("espera")}>
            <span className="sacf-ic"><Icon name="pause-circle" size={15} /></span>
            <div className="sacf-lb">
              <div className="nm">Em espera · fora do relógio <span className="mono">{f.espera.length}</span></div>
              <div className="ds">Depende do cliente, do retorno do produto ou do laudo. O SLA fica pausado, o chamado não.</div>
            </div>
            <div className="sacf-esp-tags">
              {f.esperaPorStatus.map((s) => <span key={s.id} className="tg">{s.label} <b className="mono">{s.n}</b></span>)}
            </div>
          </button>
        )}
      </div>

      <div className="sacf-detail">
        <div className="sacf-detail-head">
          <span className="tt">{selLabel}</span>
          <span className="ct mono">{listaOrd.length}</span>
          <span className="hint">ordenado por SLA mais apertado</span>
        </div>
        {listaOrd.length === 0
          ? <div className="empty" style={{ padding: 34 }}>Nenhum chamado nesta etapa.</div>
          : (
            <div className="sacf-cards">
              {listaOrd.map((c) => <Card key={c.id} chamado={c} onOpen={onOpen} showAdvance />)}
            </div>
          )}
      </div>
    </div>
  );
}

export { SacFunil };
